import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  Avatar,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import {
  fetchData,
  postQuery,
  uploadFileToS3,
} from "../../../../Requirement/services/RequirementServices";

// Styled Components
const ThreadContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "600px",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  backgroundColor: "#f9f9f9",
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "4px",
    "&:hover": {
      background: "#555",
    },
  },
}));

const MessageBubble = styled(Paper)(({ isadmin }) => ({
  padding: "12px 16px",
  maxWidth: "75%",
  alignSelf: isadmin === "true" ? "flex-start" : "flex-end",
  backgroundColor: isadmin === "true" ? "#fff" : "#e3f2fd",
  borderRadius: "12px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  position: "relative",
  border: isadmin === "true" ? "1px solid #e0e0e0" : "1px solid #90caf9",
}));

const MessageHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "8px",
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "14px",
  color: "#333",
}));

const Timestamp = styled(Typography)(({ theme }) => ({
  fontSize: "11px",
  color: "#666",
}));

const MessageText = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: "#333",
  wordWrap: "break-word",
  whiteSpace: "pre-wrap",
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: "16px",
  backgroundColor: "#fff",
  borderTop: "1px solid #e0e0e0",
  borderRadius: "0 0 8px 8px",
}));

const SubmitButton = styled("button")(({ theme }) => ({
  backgroundColor: "#de6b14",
  color: "white",
  border: "none",
  borderRadius: "6px",
  padding: "10px 24px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.3s ease",
  marginTop: "8px",
  "&:hover": {
    backgroundColor: "#c75e12",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 8px rgba(222, 107, 20, 0.3)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  "&:disabled": {
    backgroundColor: "#ccc",
    cursor: "not-allowed",
    transform: "none",
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  color: "#999",
  gap: "12px",
}));

const RemarksThreadKyc = ({ kycId, currentUserId, currentUserName }) => {
  const [remarks, setRemarks] = useState([]);
  const [newRemark, setNewRemark] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  // Sample data - Replace with actual API call
  const fetchRemarks = async () => {
    setLoading(true);
    try {
      // Simulate API call
      // await new Promise((resolve) => setTimeout(resolve, 1000));
       const userKybRemarks = await fetchData({
              target: "listUserKYBRemarks",
              userOrder: "CreatedAt",
              rowStart: 0,
              pageSize: 1000,
              filterTerms: { UserKybID: kycId, RemarksType: 2 },
            });

            console.log(userKybRemarks.items);

      
    const mappedRemarks = userKybRemarks.items.map((item, index) => ({
      id: index + 1, // Generate a simple incremental ID (or use KYBRemarksID if preferred)
      userId: item.EmailID, // Use EmailID as userId
      userName: item.EmailID.split("@")[0] || "User", // Extract username from email or default to "User"
      isAdmin: item.IsAdminUser === 1, // Convert 0/1 to boolean
      remark: item.Remarks, // Map Remarks to remark
      createdAt: new Date(item.CreatedAt).toISOString(), // Convert to ISO format
    }));

    setRemarks(mappedRemarks);

      
    } catch (error) {
      console.error("Error fetching remarks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRemarks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycId]);

  // Scroll to bottom when new remarks are added
  useEffect(() => {
    scrollToBottom();
  }, [remarks]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    const timeString = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    if (diffInDays === 0) {
      return `Today at ${timeString}`;
    } else if (diffInDays === 1) {
      return `Yesterday at ${timeString}`;
    } else if (diffInDays < 7) {
      const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
      return `${dayName} at ${timeString}`;
    } else {
      const dateStr = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: now.getFullYear() !== date.getFullYear() ? "numeric" : undefined,
      });
      return `${dateStr} at ${timeString}`;
    }
  };

  const handleSubmit = async () => {
    if (!newRemark.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      // Simulate API call to submit remark
      await new Promise((resolve) => setTimeout(resolve, 800));

      const newRemarkObject = {
        id: remarks.length + 1,
        userId: currentUserId,
        userName: currentUserName || "User",
        isAdmin: false,
        remark: newRemark.trim(),
        createdAt: new Date().toISOString(),
        
      };

      setRemarks((prevRemarks) => [...prevRemarks, newRemarkObject]);
      setNewRemark("");
      const userKYBRemarksInput = {
        EmailID: currentUserId,
        Remarks:newRemark.trim(),
        UserKybID:kycId,
        RemarksType: 2,
      };

      const kybRemarksID = uuidv4();
      await postQuery({
        target: "createUserKYBRemarks",
        userKYBRemarksInput: {
          ...userKYBRemarksInput,
          KYBRemarksID: kybRemarksID,
          
        },
      });

      // TODO: Replace with actual API call
      // const response = await fetch('PostRemarks', {
      //   body: JSON.stringify({
      //     kybId: kybId,
      //     remark: newRemark.trim(),
      //     userId: currentUserId,
      //     userName: currentUserName,
      //   })
      // });
      // const data = await response.json();
      // setRemarks(prevRemarks => [...prevRemarks, data]);
    } catch (error) {
      console.error("Error submitting remark:", error);
      alert("Failed to submit remark. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <ThreadContainer>
      {/* Header */}
      <Box
        sx={{
          padding: "16px",
          backgroundColor: "#fff",
          borderBottom: "2px solid #de6b14",
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#333", fontSize: "16px" }}
        >
          Remarks & Communication Thread
        </Typography>
        <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
          Communicate with the admin regarding your KYC verification
        </Typography>
      </Box>

      {/* Messages Container */}
      <MessagesContainer>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress sx={{ color: "#de6b14" }} />
          </Box>
        ) : remarks.length === 0 ? (
          <EmptyState>
            <AdminPanelSettingsIcon sx={{ fontSize: 64, color: "#ddd" }} />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              No remarks yet
            </Typography>
            <Typography variant="body2">
              Start a conversation with the admin below
            </Typography>
          </EmptyState>
        ) : (
          <>
            {remarks.map((remark) => (
              <MessageBubble
                key={remark.id}
                isadmin={remark.isAdmin.toString()}
                elevation={0}
              >
                <MessageHeader>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: remark.isAdmin ? "#de6b14" : "#1976d2",
                    }}
                  >
                    {remark.isAdmin ? (
                      <AdminPanelSettingsIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <PersonIcon sx={{ fontSize: 18 }} />
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <UserName>
                      {remark.userName}
                      {remark.isAdmin && (
                        <span
                          style={{
                            marginLeft: "6px",
                            fontSize: "11px",
                            fontWeight: 500,
                            color: "#de6b14",
                            backgroundColor: "#fff3e0",
                            padding: "2px 6px",
                            borderRadius: "4px",
                          }}
                        >
                          Admin
                        </span>
                      )}
                    </UserName>
                    <Timestamp>{formatDateTime(remark.createdAt)}</Timestamp>
                  </Box>
                </MessageHeader>
                <MessageText>{remark.remark}</MessageText>
              </MessageBubble>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </MessagesContainer>

      <Divider />

      {/* Input Container */}
      <InputContainer>
        <Typography
          variant="body2"
          sx={{
            marginBottom: "10px",
            fontWeight: 600,
            color: "#333",
            fontSize: "14px",
          }}
        >
          Add Your Remark
        </Typography>
        <textarea
          className="form-control"
          rows="2"
          value={newRemark}
          onChange={(e) => setNewRemark(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={submitting}
          inputProps={{ maxLength: 1000 }}
          sx={{
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              padding: "12px 14px",
              fontSize: "14px",
              lineHeight: "1.5",
              "& fieldset": {
                borderColor: "#d1d5db",
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: "#de6b14",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#de6b14",
                borderWidth: "2px",
              },
              "&.Mui-disabled": {
                backgroundColor: "#f9fafb",
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "0",
              "&::placeholder": {
                color: "#9ca3af",
                opacity: 1,
                fontSize: "13px",
              },
            },
          }}
          fullWidth
          variant="outlined"
          placeholder="Type your remark here...    (Press Enter to submit, Shift+Enter for new line)"
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#6b7280", fontSize: "12px" }}
          >
            {newRemark.length}/1000 characters
          </Typography>
          <SubmitButton
            onClick={handleSubmit}
            disabled={!newRemark.trim() || submitting}
          >
            {submitting ? "Submitting..." : "Submit Remark"}
          </SubmitButton>
        </Box>
      </InputContainer>
    </ThreadContainer>
  );
};

export default RemarksThreadKyc;
